import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { withdrawalsAPI, showConfirmModal } from '../services/api';
import { Edit, Trash2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import SearchableSelect from '../components/SearchableSelect';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Withdrawals = () => {
  const { user } = useAuth();
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [date, setDate] = useState('');
  const [amount, setAmount] = useState('');
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [editingRowId, setEditingRowId] = useState(null);
  const [editData, setEditData] = useState({});
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    fetchWithdrawals();
    fetchEmployees();
    fetchProjects();
  }, []);

  const fetchWithdrawals = async () => {
    try {
      setLoading(true);
      const data = await withdrawalsAPI.getAll();
      setWithdrawals(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to fetch withdrawals');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API_URL}/employees`);
      const emps = res.data.employees || [];
      setEmployees(emps.filter(e => e.active).sort((a, b) => a.last_name.localeCompare(b.last_name)));
    } catch (err) { console.error(err); }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API_URL}/projects`);
      const projs = Array.isArray(res.data) ? res.data : res.data.projects || [];
      setProjects(projs.sort((a, b) => (a.project_id || '').localeCompare(b.project_id || '')));
    } catch (err) { console.error(err); }
  };

  const getProjectLabel = (p) => `[${p.project_id}] ${p.customer} | ${p.job_location || ''}`;

  const isAdmin = !!user?.is_admin;
  const employeeOptions = useMemo(
    () => employees.map((emp) => ({ value: `${emp.last_name}, ${emp.first_name}`, label: `${emp.last_name}, ${emp.first_name}` })),
    [employees],
  );
  const projectOptions = useMemo(
    () => projects.map((p) => ({ value: p.project_id, label: getProjectLabel(p) })),
    [projects],
  );

  const handleSubmit = async () => {
    if (!selectedEmployee || !selectedProject || !amount || !date) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      const projObj = projects.find(p => p.project_id === selectedProject);
      const projectLabel = projObj ? `[${projObj.project_id}] ${projObj.customer} | ${projObj.job_location || ''}` : selectedProject;
      const payload = {
        employee: selectedEmployee,
        project: projectLabel,
        project_id: selectedProject,
        amount: amount.replace(/[^0-9.]/g, ''),
        date
      };
      await withdrawalsAPI.create(payload);
      toast.success('Withdrawal created');
      setSelectedEmployee('');
      setSelectedProject('');
      setAmount('');
      setDate('');
      fetchWithdrawals();
    } catch (error) {
      toast.error('Failed to save withdrawal');
    }
  };

  const handleInlineEdit = (w) => {
    setEditingRowId(w._id);
    const projId = w.project_id || (w.project || '').split(' ')[0].replace(/[\[\]]/g, '');
    setEditData({
      employee: w.employee || '',
      project_id: projId,
      amount: (w.amount || '0').toString().replace(/[$,]/g, ''),
      date: w.date || '',
    });
  };

  const handleInlineSave = async () => {
    if (!editingRowId) return;
    try {
      const projObj = projects.find(p => p.project_id === editData.project_id);
      const projectLabel = projObj ? `[${projObj.project_id}] ${projObj.customer} | ${projObj.job_location || ''}` : editData.project_id;
      await withdrawalsAPI.update(editingRowId, {
        employee: editData.employee,
        project: projectLabel,
        project_id: editData.project_id,
        amount: editData.amount.replace(/[^0-9.]/g, ''),
        date: editData.date,
      });
      toast.success('Withdrawal updated');
      setEditingRowId(null);
      setEditData({});
      fetchWithdrawals();
    } catch (error) {
      toast.error('Failed to update withdrawal');
    }
  };

  const handleInlineCancel = () => {
    setEditingRowId(null);
    setEditData({});
  };

  const handleDeleteWithdrawal = async (e, id) => {
    e.stopPropagation();
    if (!(await showConfirmModal('Are you sure you want to delete this withdrawal?'))) return;
    try {
      await withdrawalsAPI.delete(id);
      toast.success('Withdrawal deleted');
      fetchWithdrawals();
    } catch (error) {
      if (error.response?.status !== 401) toast.error('Failed to delete withdrawal');
    }
  };

  const extractProjectId = (w) => w.project_id || (w.project || '').split(' ')[0].replace(/[\[\]]/g, '');

  const uniqueEmployees = [...new Set(withdrawals.map(w => w.employee).filter(Boolean))].sort();

  const filtered = withdrawals.filter(w => {
    if (filterEmployee && w.employee !== filterEmployee) return false;
    if (filterProject) {
      const term = filterProject.toLowerCase();
      if (!(w.project || '').toLowerCase().includes(term)) return false;
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (w.employee || '').toLowerCase().includes(term) || (w.project || '').toLowerCase().includes(term);
    }
    return true;
  });

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sorted = [...filtered].sort((a, b) => {
    if (!sortColumn) return 0;
    const dir = sortDirection === 'asc' ? 1 : -1;
    if (sortColumn === 'employee') {
      return (a.employee || '').localeCompare(b.employee || '') * dir;
    }
    if (sortColumn === 'date') {
      return (a.date || '').localeCompare(b.date || '') * dir;
    }
    return 0;
  });

  const renderSortIcon = (column) => (
    <span className={`ml-1 text-[10px] ${sortColumn === column ? 'text-gray-700' : 'text-gray-300'}`}>
      {sortColumn === column ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
    </span>
  );

  const fmtAmount = (val) => {
    const n = parseFloat((val || '0').toString().replace(/[$,]/g, ''));
    return isNaN(n) ? '$0.00' : '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <Layout>
      <div className="p-4 lg:p-8">
        <div className="mb-6">
          <h2 className="text-2xl text-[#5B8DB8] font-light italic" data-testid="withdrawals-subtitle">Employees</h2>
          <h1 className="text-3xl text-[#5B8DB8] font-light" data-testid="withdrawals-title">Employee Commission Withdrawals</h1>
        </div>

        <div className="bg-white rounded shadow p-6">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left: Form (admin only) */}
            {isAdmin ? (
            <div className="lg:w-[320px] flex-shrink-0" data-testid="withdrawal-form">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <label className="text-red-400 text-sm w-20 text-right flex-shrink-0">Employee:</label>
                  <div className="w-52">
                    <SearchableSelect
                      value={selectedEmployee}
                      onChange={setSelectedEmployee}
                      options={employeeOptions}
                      testId="employee-select"
                      placeholder="-- select employee --"
                      emptyText="No employees match"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-red-400 text-sm w-20 text-right flex-shrink-0">Project:</label>
                  <div className="w-52">
                    <SearchableSelect
                      value={selectedProject}
                      onChange={setSelectedProject}
                      options={projectOptions}
                      testId="project-select"
                      placeholder="-- select project --"
                      emptyText="No projects match"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-red-400 text-sm w-20 text-right flex-shrink-0">Date:</label>
                  <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm w-40" data-testid="date-input" />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-red-400 text-sm w-20 text-right flex-shrink-0">Amount:</label>
                  <input type="text" value={amount} onChange={(e) => setAmount(e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm w-40" data-testid="amount-input" />
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-20 flex-shrink-0"></div>
                  <button className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white text-sm px-5 py-2 rounded transition-colors" onClick={handleSubmit} data-testid="save-btn">
                    Save
                  </button>
                  <button className="bg-gray-400 hover:bg-gray-500 text-white text-sm px-5 py-2 rounded transition-colors" onClick={() => { setSelectedEmployee(''); setSelectedProject(''); setDate(''); setAmount(''); }} data-testid="clear-btn">
                    Clear
                  </button>
                </div>
              </div>
            </div>
            ) : (
            <div className="lg:w-[320px] flex-shrink-0 border border-gray-200 rounded p-4 bg-gray-50 text-sm text-gray-500 italic" data-testid="withdrawal-form-readonly">
              Only admins can record or edit commission withdrawals. You can view the recorded payments on the right.
            </div>
            )}

            {/* Right: Table */}
            <div className="flex-1 min-w-0" data-testid="withdrawal-table-section">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  Show
                  <select value={entriesPerPage} onChange={(e) => setEntriesPerPage(Number(e.target.value))} className="border border-gray-300 rounded px-2 py-1 text-sm">
                    <option value={10}>10</option><option value={50}>50</option><option value={100}>100</option>
                  </select>
                  entries
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  Search:
                  <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="border border-gray-300 rounded px-3 py-1 text-sm w-40" data-testid="search-input" />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="withdrawals-table">
                  <thead>
                    <tr className="border-b-2">
                      <th className="text-left p-2 text-gray-600 font-normal cursor-pointer select-none" onClick={() => handleSort('employee')} data-testid="sort-employee">
                        <span className="inline-flex items-center">Employee{renderSortIcon('employee')}</span>
                      </th>
                      <th className="text-left p-2 text-gray-600 font-normal">Project</th>
                      <th className="text-right p-2 text-gray-600 font-normal">Amount</th>
                      <th className="text-left p-2 text-gray-600 font-normal cursor-pointer select-none" onClick={() => handleSort('date')} data-testid="sort-date">
                        <span className="inline-flex items-center">Date{renderSortIcon('date')}</span>
                      </th>
                      <th className="w-16"></th>
                    </tr>
                    {/* Filter row */}
                    <tr className="border-b">
                      <td className="p-2">
                        <select value={filterEmployee} onChange={(e) => setFilterEmployee(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-xs w-full" data-testid="filter-employee">
                          <option value=""></option>
                          {uniqueEmployees.map(e => <option key={e} value={e}>{e}</option>)}
                        </select>
                      </td>
                      <td className="p-2">
                        <input type="text" value={filterProject} onChange={(e) => setFilterProject(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-xs w-full" data-testid="filter-project" />
                      </td>
                      <td className="p-2"></td>
                      <td className="p-2"></td>
                      <td className="p-2"></td>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan="5" className="text-center py-8 text-gray-500">Loading...</td></tr>
                    ) : sorted.length === 0 ? (
                      <tr><td colSpan="5" className="text-center py-8 text-gray-500">No withdrawals found</td></tr>
                    ) : (
                      sorted.slice(0, entriesPerPage).map((w, index) => {
                        const isEditing = editingRowId === w._id;
                        const projId = extractProjectId(w);

                        if (isEditing) {
                          return (
                            <tr key={w._id || index} className="border-b bg-blue-50" data-testid={`withdrawal-row-${index}`}>
                              <td className="p-1">
                                <SearchableSelect
                                  value={editData.employee}
                                  onChange={(v) => setEditData(prev => ({ ...prev, employee: v }))}
                                  options={
                                    editData.employee && !employeeOptions.some(o => o.value === editData.employee)
                                      ? [{ value: editData.employee, label: editData.employee }, ...employeeOptions]
                                      : employeeOptions
                                  }
                                  testId={`inline-employee-select-${index}`}
                                  placeholder="-- select employee --"
                                  emptyText="No employees match"
                                />
                              </td>
                              <td className="p-1">
                                <SearchableSelect
                                  value={editData.project_id}
                                  onChange={(v) => setEditData(prev => ({ ...prev, project_id: v }))}
                                  options={
                                    editData.project_id && !projectOptions.some(o => o.value === editData.project_id)
                                      ? [{ value: editData.project_id, label: editData.project_id }, ...projectOptions]
                                      : projectOptions
                                  }
                                  testId={`inline-project-select-${index}`}
                                  placeholder="-- select project --"
                                  emptyText="No projects match"
                                />
                              </td>
                              <td className="p-1">
                                <input type="text" value={editData.amount} onChange={(e) => setEditData(prev => ({ ...prev, amount: e.target.value }))} className="border border-gray-300 rounded px-2 py-1.5 text-sm w-24 text-right" />
                              </td>
                              <td className="p-1">
                                <input type="date" value={editData.date} onChange={(e) => setEditData(prev => ({ ...prev, date: e.target.value }))} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
                              </td>
                              <td className="p-1">
                                <div className="flex items-center gap-1">
                                  <button onClick={handleInlineSave} className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white rounded p-1.5" data-testid={`inline-save-btn-${index}`}>
                                    <Check className="w-3.5 h-3.5" />
                                  </button>
                                  <button onClick={handleInlineCancel} className="bg-gray-400 hover:bg-gray-500 text-white rounded p-1.5" data-testid={`inline-cancel-btn-${index}`}>
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        }

                        return (
                          <tr key={w._id || index} className="border-b hover:bg-gray-50" data-testid={`withdrawal-row-${index}`}>
                            <td className="p-2 text-gray-700 whitespace-nowrap">{w.employee}</td>
                            <td className="p-2 text-gray-700">
                              {projId ? (
                                <Link to={`/projects/${projId}`} className="text-[#5B8DB8] hover:underline">{w.project}</Link>
                              ) : (
                                w.project
                              )}
                            </td>
                            <td className="p-2 text-gray-700 text-right whitespace-nowrap">{fmtAmount(w.amount)}</td>
                            <td className="p-2 text-gray-700 whitespace-nowrap">{w.date}</td>
                            <td className="p-2">
                              <div className="flex items-center gap-1">
                                {isAdmin && (
                                  <button onClick={() => handleInlineEdit(w)} className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white rounded p-1.5" data-testid={`edit-btn-${index}`}>
                                    <Edit className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                {isAdmin && (
                                  <button onClick={(e) => handleDeleteWithdrawal(e, w._id)} className="text-[#DC3545] hover:text-[#C82333]" data-testid={`delete-btn-${index}`}>
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Withdrawals;
